import * as React from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import {NumericFormat} from "react-number-format";
import {muiGridLocales} from "../../../../helpers/mui/muiLocales";
import {CustomPagination} from "../../../../components/mui/grids/dataGridCustom/customPagination";
import {DataGridPremium} from "@mui/x-data-grid-premium";
import {GridToolbar, initialFilters, mapFilters} from "../../pages/index";
import {useTranslation} from "react-i18next";
import {ISSUES_GLOBAL} from "../../locales/namespaces";
import useUserData from "../../../../hooks/user/useUserData";
import {useEffect, useMemo, useState} from "react";
import TruncateDescription from "../../../../pages/dashboard/partials/truncateDescription";
import CardChip from "../../../../pages/dashboard/partials/cardChip";
import {formControlDefaults, numericFormatDigitDefaults} from "../../../../helpers/theme/defaultProps";
import {withPermissionHOC} from "../../../permission/components/permissionHOC/permissionHOC";
import {view_po} from "../../../../helpers/rights/rights";
import axios from "axios";
import {API} from "../../../../helpers/constants";
import _ from "lodash";
import {enqueueSnackbar} from "notistack";
import {handleAxiosErrors, logoutUnauthorizedUser} from "../../../../helpers/errorHandling";
import {LoadingButton} from "@mui/lab";
import TextField from "@mui/material/TextField";
import CheckBox from "../../../../components/custom/general/tree/components/checkBox/checkBox";
import {Checkbox, FormControl, FormControlLabel, FormLabel} from "@mui/material";

const SOCust = ({
                    open,
                    onClose,
                    data,
                    rowSelectionModel,
                    dataManager,
                    setLastSearchFilters,
                    setRowSelectionModel,
                    setFilters,
                    initialFilters,
                    filters,
    loadingIndex
}) => {
    const { t } = useTranslation(ISSUES_GLOBAL)
    const {language,decimalSeparator, thousandSeparator,token } = useUserData()
    const [loading,setLoading] = useState(false)
    const [loadingSO,setLoadingSO] = useState(false)
    const [poDescription,setPoDescription] = useState('')
    const [poCode,setPoCode] = useState('')
    const [total,setTotal] = useState('')
    const [soDescription,setSoDescription] = useState('')
    const [rows, setRows] = useState([]);
    const [hasOneDescription, setHasOneDescription] = useState(false);
    const [oneDescription, setOneDescription] = useState('');

    useEffect(() => {
        const mappedRows = (data || []).map((row) => ({
            ...row,
            EditableChargeCost:
                row.EditableChargeCost != null
                    ? Number(row.EditableChargeCost)
                    : Number(row.ActualCost) || 0,
        }));

        setRows(mappedRows);
    }, [data]);

    const isPoCodeInvalid = useMemo(() => {
        if (poCode.trim() === '') return false;
        return (data || []).some((row) => row.CPOCode !== poCode);
    }, [poCode, data]);

    const totalChargeCost = React.useMemo(() => {
        return (rows || []).reduce((sum, row) => {
            return sum + (Number(row.ActualCost) || 0);
        }, 0);
    }, [rows]);

    const totalEditableChargeCost = React.useMemo(() => {
        return (rows || []).reduce((sum, row) => {
            return sum + (Number(row.EditableChargeCost) || 0);
        }, 0);
    }, [rows]);

    const totalRow = useMemo(() => ({
        Code: t("Total"),
        Description: "",
        CustName: "",
        CPOCode:"",
        Address: "",
        ActualCost: totalChargeCost,
        EditableChargeCost: totalEditableChargeCost,
    }), [totalChargeCost,totalEditableChargeCost, t]);

    const columns = useMemo(() => [
        {
            field: 'Code',
            headerName: t('Code'),
            description: t('Code'),
            minWidth: 100,
            flex: 1,
            renderCell: (params) => {
                const isTotal = params.value === t("Total");
                return (
                    <Typography fontWeight={isTotal ? 700 : 400}>
                        {params.value}
                    </Typography>
                );
            }
        },
        {
            field: 'Description',
            headerName: t('Description'),
            description: t('Description'),
            minWidth: 200,
            flex: 1,
            renderCell: (params) => {
                return <TruncateDescription value={params.value}/>
            },
        },
        {
            field: 'CustName',
            headerName: t('Customer'),
            description: t('Customer'),
            minWidth: 250,
            flex: 1,
            renderCell: (params) => {
                return <TruncateDescription value={params.value}/>
            },
        },
        {
            field: 'CPOCode',
            headerName: t('PO Cust Code'),
            description: t('PO Cust Code'),
            minWidth: 120,
            flex: 1,
        },
        {
            field: 'Address',
            headerName: t('Site'),
            description: t('Site'),
            minWidth: 250,
            flex: 1,
            renderCell: (params) => {
                return <TruncateDescription value={params.value}/>
            },
        },
        {
            field: 'ActualCost',
            headerName: t('Charge'),
            description: t('Charge'),
            minWidth: 100,
            type: 'number',
            flex: 1,
            valueGetter: (value, row, column, apiRef) => value ? Number(value) : 0,
            renderCell: (params) => {
                const isTotal = params.row.Code === t("Total");

                return (
                    <NumericFormat
                        {...numericFormatDigitDefaults}
                        thousandSeparator={thousandSeparator}
                        decimalSeparator={decimalSeparator}
                        displayType="text"
                        value={params.value}
                        style={{ fontWeight: isTotal ? 700 : 400 }}
                    />
                );
            }

        },
        {
            field: 'EditableChargeCost',
            headerName: t('Editable Charge'),
            description: t('Editable Charge'),
            minWidth: 140,
            type: 'number',
            flex: 1,
            editable: true,
            valueGetter: (value) => value ? Number(value) : 0,
            valueParser: (value) => {
                if (value == null || value === '') return 0;
                return Number(String(value).replace(',', '.')) || 0;
            },
            renderCell: (params) => {
                const isTotal = params.row.Code === t("Total");

                return (
                    <NumericFormat
                        {...numericFormatDigitDefaults}
                        thousandSeparator={thousandSeparator}
                        decimalSeparator={decimalSeparator}
                        displayType="text"
                        value={params.value}
                        style={{ fontWeight: isTotal ? 700 : 400 }}
                    />
                );
            }
        }

    ], [t])

    const handleCreatePOandSO = () => {
        if (isPoCodeInvalid) return;

        const chargesArray = rows.map((row) => ({
            id: row.id,
            value: row.EditableChargeCost,
        }));

        setLoading(true)
        axios({
            method: 'post',
            data: {
                CustomerId:data[0].CustId,
                //issues: rowSelectionModel,
                issues: chargesArray,
                Description:poDescription,
                SODescription:soDescription,
                Total:total,
                Code:poCode,
                hasOneDescription:hasOneDescription,
                oneDescription:oneDescription,
                isSaved:0
            },
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            url: `${API}/en/custom/customer/purchase/workOrder/store`
        }).then((res) => {
            const onSuccess = () => {
                //setLastSearchFilters(null);
                setRowSelectionModel([]);
                setTotal('')
                setPoCode('')
                setSoDescription('')
                setPoDescription('')
                //setFilters(initialFilters)
                onClose()
                enqueueSnackbar(t('S.O and P.O customer created successfully!'), {
                    variant: 'success',
                    autoHideDuration: 3000,
                })
                setLoading(false)
            }
            const params =  { ...mapFilters(filters) };
            dataManager(params, onSuccess)
        }).catch((error) => {
            setLoading(false)
            enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
                variant: 'error',
                autoHideDuration: 3000,
            })

        })
    }
    const handleCreateAndSave = () => {
        if (isPoCodeInvalid) return;

        const chargesArray = rows.map((row) => ({
            id: row.id,
            value: row.EditableChargeCost,
        }));

        setLoadingSO(true)
        axios({
            method: 'post',
            data: {
                CustomerId:data[0].CustId,
                //issues: rowSelectionModel,
                issues: chargesArray,
                Description:poDescription,
                SODescription:soDescription,
                Total:total,
                Code:poCode,
                hasOneDescription:hasOneDescription,
                oneDescription:oneDescription,
                isSaved:1
            },
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            url: `${API}/en/custom/customer/purchase/workOrder/store`
        }).then((res) => {
            const onSuccess = () => {
                //setLastSearchFilters(null);
                setRowSelectionModel([]);
                setSoDescription('')
                setTotal('')
                setPoCode('')
                setPoDescription('')
                //setFilters(initialFilters)
                onClose()
                enqueueSnackbar(t('S.O created successfully!'), {
                    variant: 'success',
                    autoHideDuration: 3000,
                })
                setLoadingSO(false)
            }
            const params =  { ...mapFilters(filters) };
            dataManager(params, onSuccess)
        }).catch((error) => {
            setLoadingSO(false)
            enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
                variant: 'error',
                autoHideDuration: 3000,
            })

        })
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle>
                {t('Create S.O')}
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ mt: 2 }}>
                {(!data || data.length === 0) ? (
                    <Typography color="text.secondary">
                        {t('No selected tickets')}
                    </Typography>
                ) : (
                    <>
                        <Box sx={{display:'flex', justifyContent:'space-between',mb:2}}>
                            <Typography sx={{ mb: 1 }}>
                               <strong> {data[0].CustName} </strong>
                            </Typography>
                            <Typography sx={{mb:1}}>
                                <strong>{t('P.O Cust Information')}</strong>
                            </Typography>
                        </Box>
                        <Box sx={{display:'flex',justifyContent:'space-between'}}>
                            <Box sx={{mt:1,mb:2, width:'40%'}}>
                                <TextField
                                    label={t("Description")}
                                    name="soDescription"
                                    value={soDescription}
                                    onChange={(e) => setSoDescription(e.target.value.slice(0, 200))}
                                    inputProps={{ maxLength: 200 }}
                                    helperText={`${soDescription.length}/200`}
                                    multiline
                                    minRows={2}
                                    fullWidth
                                    variant="outlined"
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    placeholder={t("Enter S.O description")}
                                    sx={{
                                        "& .MuiInputLabel-root": {
                                            color: "#004B88",
                                            fontWeight: 500,
                                        },
                                        "& .MuiOutlinedInput-root": {
                                            alignItems: "flex-start",
                                            backgroundColor: "#fff",
                                            "& fieldset": {
                                                borderColor: "#004B88",
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "#004B88",
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "#004B88",
                                                borderWidth: 2,
                                            },
                                        },
                                        "& .MuiOutlinedInput-input": {
                                            paddingTop: "10px",
                                        },
                                    }}
                                />
                                <Box>
                                    <Box>
                                        <FormControl
                                            {...formControlDefaults}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={hasOneDescription}
                                                        onChange={(evt)=>setHasOneDescription(evt.target.checked)}
                                                        name="hasOneDescription"
                                                    />
                                                }
                                                label={<FormLabel>{t('Has one description')}</FormLabel>}
                                            />
                                        </FormControl>
                                    </Box>
                                    {hasOneDescription &&
                                        <Box sx={{display: 'flex', gap: 2, mt: 2}}>
                                        <TextField
                                            label={t("Description")}
                                            name="oneDescription"
                                            value={oneDescription}
                                            onChange={(e) => setOneDescription(e.target.value.slice(0, 200))}
                                            inputProps={{maxLength: 200}}
                                            helperText={`${oneDescription.length}/200`}
                                            multiline
                                            minRows={2}
                                            fullWidth
                                            variant="outlined"
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            placeholder={t("One description")}
                                            sx={{
                                                "& .MuiInputLabel-root": {
                                                    color: "#004B88",
                                                    fontWeight: 500,
                                                },
                                                "& .MuiOutlinedInput-root": {
                                                    alignItems: "flex-start",
                                                    backgroundColor: "#fff",
                                                    "& fieldset": {
                                                        borderColor: "#004B88",
                                                    },
                                                    "&:hover fieldset": {
                                                        borderColor: "#004B88",
                                                    },
                                                    "&.Mui-focused fieldset": {
                                                        borderColor: "#004B88",
                                                        borderWidth: 2,
                                                    },
                                                },
                                                "& .MuiOutlinedInput-input": {
                                                    paddingTop: "10px",
                                                },
                                            }}
                                        />
                                    </Box>}
                                </Box>
                            </Box>
                            <Box sx={{mt:1,mb:2,width:'40%',display:'grid'}}>
                                <Box sx={{display:'flex',gap:2,mb:2}}>
                                    <TextField
                                        label={t("PO Code")}
                                        name="poCode"
                                        value={poCode}
                                        onChange={(e) => setPoCode(e.target.value)}
                                        error={isPoCodeInvalid}
                                        helperText={isPoCodeInvalid ? t('PO Code must match the PO Cust Code of all the selected tickets') : ''}
                                        fullWidth
                                        variant="outlined"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        sx={{
                                            "& .MuiInputLabel-root": {
                                                color: "#004B88",
                                                fontWeight: 800,
                                                fontSize:14
                                            },
                                            "& .MuiOutlinedInput-root": {
                                                alignItems: "flex-start",
                                                backgroundColor: "#fff",
                                                "& fieldset": {
                                                    borderColor: "#004B88",
                                                },
                                                "&:hover fieldset": {
                                                    borderColor: "#004B88",
                                                },
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "#004B88",
                                                    borderWidth: 2,
                                                },
                                            },
                                            "& .MuiOutlinedInput-input": {
                                                paddingTop: "10px",
                                                mb:1,
                                                ml:1
                                            },
                                        }}
                                    />
                                    <NumericFormat
                                        label={t('Total')}
                                        value={total}
                                        variant={'outlined'}
                                        id="total"
                                        onChange={(e)=>setTotal(e.target.value)}
                                        inputProps={{maxLength: 4}}
                                        thousandSeparator={''}
                                        decimalSeparator={decimalSeparator}
                                        //onValueChange={}
                                        decimalScale={0}
                                        allowNegative={false}
                                        customInput={TextField}
                                        size={'small'}
                                        allowLeadingZeros={true}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        sx={{
                                            "& .MuiInputLabel-root": {
                                                color: "#004B88",
                                                fontWeight: 800,
                                                fontSize:14
                                            },
                                            "& .MuiOutlinedInput-root": {
                                                alignItems: "flex-start",
                                                backgroundColor: "#fff",
                                                "& fieldset": {
                                                    borderColor: "#004B88",
                                                },
                                                "&:hover fieldset": {
                                                    borderColor: "#004B88",
                                                },
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "#004B88",
                                                    borderWidth: 2,
                                                },
                                            },
                                            "& .MuiOutlinedInput-input": {
                                                paddingTop: "10px",
                                                mb:1,
                                                ml:1
                                            },
                                        }}
                                    />
                                </Box>
                                <TextField
                                    label={t("Description")}
                                    name="poDescription"
                                    value={poDescription}
                                    onChange={(e) => setPoDescription(e.target.value.slice(0, 200))}
                                    inputProps={{ maxLength: 200 }}
                                    helperText={`${poDescription.length}/200`}
                                    multiline
                                    minRows={2}
                                    fullWidth
                                    variant="outlined"
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    placeholder={t("Enter P.O description")}
                                    sx={{
                                        "& .MuiInputLabel-root": {
                                            color: "#004B88",
                                            fontWeight: 500,
                                        },
                                        "& .MuiOutlinedInput-root": {
                                            alignItems: "flex-start",
                                            backgroundColor: "#fff",
                                            "& fieldset": {
                                                borderColor: "#004B88",
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "#004B88",
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "#004B88",
                                                borderWidth: 2,
                                            },
                                        },
                                        "& .MuiOutlinedInput-input": {
                                            paddingTop: "10px",
                                        },
                                    }}
                                />
                            </Box>
                        </Box>


                        <Box>
                            <DataGridPremium
                                rows={rows}
                                columns={columns}
                                getRowId={(row)=>row.Code}
                                pinnedRows={{
                                    bottom: [totalRow],
                                }}
                                processRowUpdate={(newRow) => {
                                    const updatedRow = {
                                        ...newRow,
                                        EditableChargeCost: Number(newRow.EditableChargeCost) || 0,
                                    };

                                    setRows((prev) =>
                                        prev.map((row) => (row.Code === updatedRow.Code ? updatedRow : row))
                                    );

                                    return updatedRow;
                                }}
                                hideFooterPagination
                                hideFooterSelectedRowCount
                                initialState={{
                                    density: 'compact'
                                }}
                                slots={{
                                    //pagination: CustomPagination,
                                }}
                                slotProps={{
                                    toolbar: {
                                        t: t,
                                    }
                                }}
                                disableSelectionOnClick
                                localeText={muiGridLocales(language)}
                            />
                        </Box>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{display:'flex',justifyContent:'space-between'}}>
                <Box sx={{display:'flex',gap:2}}>
                    <LoadingButton
                        loading={loadingIndex || loadingSO}
                        variant="contained"
                        color="warning"
                        disabled={!data || data.length === 0 || isPoCodeInvalid}
                        onClick={() =>handleCreateAndSave()}
                    >
                        {t('Store and save')}
                    </LoadingButton>
                    <LoadingButton
                        loading={loadingIndex || loading || loadingSO}
                        variant="contained"
                        color="warning"
                        disabled={!data || data.length === 0 || isPoCodeInvalid}
                        onClick={() => handleCreatePOandSO()}
                    >
                        {t('Create')}
                    </LoadingButton>
                </Box>
                    <LoadingButton
                        loading={loadingIndex || loading || loadingSO}
                        disabled={!data || data.length === 0}
                        onClick={()=>{
                            onClose()
                            setSoDescription('')
                            setPoDescription('')
                            setTotal('')
                            setPoCode('')
                        }}
                        variant="outlined"
                        color={'error'}
                    >
                        {t('Cancel')}
                    </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default withPermissionHOC(SOCust,[view_po]);
